<template>
  <div class="relative top-0 w-full">
    <!-- canvas app -->
    <div ref="target" />

    <!-- progress -->
    <div
      v-if="props.frames.length > 1"
      v-show="progress <= 99"
      class="absolute z-10 p-2 text-sm font-semibold text-center text-white transform -translate-s-1/2 bg-[#0f0f0f] rounded w-18 start-1/2 bottom-10 bg-opacity-70"
    >
      {{ progress }} %
    </div>
  </div>
</template>

<script setup>
import * as PIXI from "pixi.js-legacy";
import { Tween, Ease } from "@createjs/tweenjs";

window.PIXI = PIXI;
window.Tween = Tween;
window.Ease = Ease;

window.PIXI.settings.RENDER_OPTIONS.hello = false;

const props = defineProps({
  frames: {
    type: Array,
    required: true,
  },
  autoAnimate: {
    type: Boolean,
    default: false,
  },
  width: {
    type: Number,
    default: 1600,
  },
  height: {
    type: Number,
    default: 950,
  },
});
const app = ref(null);
const currentFrame = ref(0);
const dragging = ref(null);
const lastX = ref(0);
const progress = ref(0);
// sprites
const animatedProductSprite = ref(null);
const hintSprite = ref(null);
const placeholderImage = ref(null);
const pixiApp = ref(null);
const target = ref(null);

onUnmounted(() => {
  if (animatedProductSprite.value) {
    animatedProductSprite.value.destroy();
  }

  if (pixiApp.value) {
    pixiApp.value.destroy();
  }
});

onMounted(() => {
  if (process.browser) {
    initApp();
  }
});

async function initApp() {
  pixiApp.value = new window.PIXI.Application({
    width: props.width,
    height: props.height,
    antialias: true,
    backgroundAlpha: 0,
    resolution: 1,
    autoDensity: false,
  });

  // pixiApp.value.renderer.autoDensity = true;

  target.value.appendChild(pixiApp.value.view);

  createPlaceholderImage();

  await window.PIXI.Assets.load(props.frames, onProgress);
  placeholderImage.value.destroy();
  createProductSprite();

  // pixiApp.value.loader.onProgress.add((a) => {
  //   progress.value = Math.round(a.progress);
  // });
}

function onProgress(a) {
  progress.value = Math.round(a * 100);
}

function createProductSprite() {
  const frames = props.frames.map((f) => window.PIXI.Texture.from(f));

  if (pixiApp.value) {
    animatedProductSprite.value = new window.PIXI.AnimatedSprite(frames);
    animatedProductSprite.value.x = pixiApp.value.renderer.width / 2;
    animatedProductSprite.value.y = pixiApp.value.renderer.height / 2;
    animatedProductSprite.value.anchor.set(0.5);
    animatedProductSprite.value.loop = false;
    animatedProductSprite.value.animationSpeed = 0.35;
    animatedProductSprite.value.interactive = true;
    animatedProductSprite.value.buttonMode = false;
    pixiApp.value.stage.addChild(animatedProductSprite.value);
  }

  animatedProductSprite.value.onComplete = () => init360();

  if (props.autoAnimate) {
    animatedProductSprite.value.gotoAndPlay(0);
  } else {
    init360();
  }
}

function init360() {
  createHintSprite();
  if (props.frames.length > 1) {
    animatedProductSprite.value.cursor = "grab";
  }

  animatedProductSprite.value
    .on("pointerdown", onDragStart)
    .on("pointerup", onDragEnd)
    .on("pointerupoutside", onDragEnd)
    .on("pointermove", onDragMove);
}

function createPlaceholderImage() {
  const img = new window.PIXI.Sprite(window.PIXI.Texture.from(props.frames[0]));
  img.x = pixiApp.value.renderer.width / 2;
  img.y = pixiApp.value.renderer.height / 2;
  img.anchor.set(0.5);
  img.interactive = false;
  img.buttonMode = false;

  placeholderImage.value = img;
  pixiApp.value.stage.addChild(placeholderImage.value);
}

async function createHintSprite() {
  if (props.frames.length > 1) {
    // const HandIcon = await import("assets/svg/heroicons/hand.svg");
    const texture = window.PIXI.Texture.from("/hand.svg");

    const hintSpriteData = new window.PIXI.Sprite(texture);
    const hintSize = 200;
    const origin = pixiApp.value.renderer.width / 2 - hintSize / 2;
    hintSpriteData.interactive = false;
    hintSpriteData.height = hintSize;
    hintSpriteData.width = hintSize;
    hintSpriteData.loop = false;
    hintSpriteData.anchor.set(0.5);
    hintSpriteData.x = origin;
    hintSpriteData.y = pixiApp.value.renderer.height / 2;
    hintSprite.value = hintSpriteData;

    pixiApp.value.stage.addChild(hintSprite.value);

    window.Tween.get(hintSprite.value, { loop: 2 })
      .to({ x: origin + 200 }, 800, window.Ease.getPowInOut(2))
      .to({ x: origin }, 600, window.Ease.getPowInOut(2))
      .on("complete", () => {
        window.Tween.get(hintSprite.value).to({ alpha: 0 }, 200);
      });
  }
}

function onDragStart(event) {
  if (hintSprite.value) {
    hintSprite.value.visible = false;
  }
  dragging.value = event.data;
}

function onDragMove(event) {
  if (props.frames.length > 1) {
    if (dragging.value) {
      animatedProductSprite.value.cursor = "grabbing";

      const position = dragging.value.getLocalPosition(
        animatedProductSprite.value
      );
      const deltaX = position.x - lastX.value;
      const absoluteDx = Math.abs(deltaX);
      const speed = 5;

      if (absoluteDx > speed) {
        currentFrame.value += (deltaX * 1) / absoluteDx;

        if (currentFrame.value < 0) {
          currentFrame.value = props.frames.length - 1;
        } else if (currentFrame.value > props.frames.length - 1) {
          currentFrame.value = 0;
        }
        animatedProductSprite.value.gotoAndStop(currentFrame.value);
        lastX.value = position.x;
      }
    }
  }
}

function onDragEnd() {
  if (props.frames.length > 1) {
    dragging.value = null;
    animatedProductSprite.value.cursor = "grab";
  }
}
</script>

<style lang="postcss">
canvas {
  @apply w-[390px] sm:w-[480px] md:w-full  max-w-full mx-auto;
}
</style>
